import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import ResourceFileCard from './ResourceFileCard';
import ResourceUploadModal from './ResourceUploadModal';
import { File as FileIcon, Plus, BookOpen } from 'lucide-react';
import { getResourceConfig } from '@/config/resourceConfig';

/**
 * Admin file storage for Professional Development source assets (S3).
 * Live packages are wired in src/data/resourceLibraryPackages.js until catalog sync ships.
 */
const ResourceLibraryManager = ({
  files,
  resourceType,
  onUpload,
  onDelete,
  uploadProgress,
}) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [newFile, setNewFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const config = getResourceConfig(resourceType);
  const {
    icon: Icon,
    iconBgColor,
    iconColor,
    emptyStateTitle,
    emptyStateMessage,
    uploadButtonLabel,
  } = config;

  const handleFileView = (file) => {
    if (file.url) window.open(file.url, '_blank');
  };

  const handleFileDownload = (file) => {
    if (file.url) window.open(file.url, '_blank');
  };

  const handleFileDelete = (file) => {
    if (window.confirm('Delete this file from the Resource Library?')) {
      onDelete(file.key, resourceType);
    }
  };

  const checkDuplicateFile = (fileName) => {
    const normalizedNewFileName = fileName.toLowerCase().trim();
    return files.some((existingFile) => {
      const keyParts = existingFile.key.split('/');
      const s3FileName = keyParts[keyParts.length - 1];
      let originalFileName = s3FileName;
      if (s3FileName.includes('_')) {
        const parts = s3FileName.split('_');
        if (parts.length > 1 && /^\d+$/.test(parts[0])) {
          originalFileName = parts.slice(1).join('_');
        }
      }
      return originalFileName.toLowerCase().trim() === normalizedNewFileName;
    });
  };

  const handleUpload = async () => {
    if (!newFile) return;
    const selectedFiles = Array.isArray(newFile) ? newFile : [newFile];
    const filesToUpload = [];
    const duplicateFiles = [];

    for (const file of selectedFiles) {
      if (checkDuplicateFile(file.name)) duplicateFiles.push(file.name);
      else filesToUpload.push(file);
    }

    if (duplicateFiles.length > 0 && filesToUpload.length === 0) {
      alert(`File(s) already exist: ${duplicateFiles.join(', ')}`);
      return;
    }

    setIsUploading(true);
    try {
      for (let i = 0; i < filesToUpload.length; i++) {
        await onUpload(filesToUpload[i], resourceType, null, i, filesToUpload.length);
      }
      setNewFile(null);
      setShowUploadModal(false);
    } catch (error) {
      console.error('Resource Library upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-4 bg-emerald-50/80 border-emerald-200">
        <div className="flex gap-3">
          <BookOpen className="w-5 h-5 text-emerald-800 shrink-0 mt-0.5" />
          <div className="text-sm text-emerald-900 space-y-2">
            <p className="font-semibold">Professional Development (source files)</p>
            <ul className="list-disc pl-4 space-y-1 text-emerald-800">
              <li>Store <strong>.pptx</strong> decks and <strong>content.json</strong> drafts in S3 for your team</li>
              <li>
                To ship a package on the site, add it to{' '}
                <code className="bg-white/60 px-1 rounded">src/data/resourceLibraryPackages.js</code> and
                deploy
              </li>
            </ul>
            <p className="text-xs text-emerald-700">
              S3 path: <code className="bg-white/60 px-1 rounded">resources/resource-library/&#123;slug&#125;/</code>
            </p>
          </div>
        </div>
      </Card>

      <div className="flex flex-wrap justify-end gap-3">
        <Button onClick={() => setShowUploadModal(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          {uploadButtonLabel}
        </Button>
      </div>

      {uploadProgress && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md">
          <p className="text-emerald-800 text-sm">{uploadProgress}</p>
        </div>
      )}

      {files.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {files.map((file) => (
            <ResourceFileCard
              key={file.key}
              file={file}
              onView={handleFileView}
              onDownload={handleFileDownload}
              onDelete={handleFileDelete}
              icon={Icon}
              iconBgColor={iconBgColor}
              iconColor={iconColor}
            />
          ))}
        </div>
      )}

      {files.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <div className={`p-4 ${iconBgColor} rounded-full mb-4`}>
            <FileIcon className={`w-8 h-8 ${iconColor}`} />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{emptyStateTitle}</h3>
          <p className="text-sm text-gray-600 text-center max-w-md">{emptyStateMessage}</p>
        </div>
      )}

      <ResourceUploadModal
        isOpen={showUploadModal}
        onClose={() => !isUploading && setShowUploadModal(false)}
        onUpload={handleUpload}
        resourceType={resourceType}
        file={newFile}
        setFile={setNewFile}
        isUploading={isUploading}
        checkDuplicate={checkDuplicateFile}
      />
    </div>
  );
};

export default ResourceLibraryManager;
